// camera.ts — turning a scan's recorded cameras (OpenCV world→camera poses
// plus pinhole intrinsics, ARCHITECTURE.md §2) into what the GL splat stack
// needs: a GL view matrix, a projection whose principal point matches the
// photo's, the camera centre for the trajectory line, and the four
// image-corner rays of the viewing frustum. Lens distortion is ignored: the
// render is the ideal pinhole image, the keyframe JPEG is the raw one.

import type { Mat4, Vec3 } from './math';
import { poseCameraCenter } from './math';

// CameraPose is world→camera, R row-major (Xc = R·Xw + T), as the API sends.
export interface CameraPose {
  R: ArrayLike<number>;
  T: ArrayLike<number>;
}

export interface PinholeIntrinsics {
  width: number;
  height: number;
  fx: number;
  fy: number;
  cx: number;
  cy: number;
}

// poseToGLView converts an OpenCV pose (x right, y down, z forward) into a GL
// view matrix (x right, y up, looking down −z): flip the y and z rows.
export function poseToGLView(p: CameraPose): Mat4 {
  const m = new Float32Array(16);
  const sign = [1, -1, -1];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) m[c * 4 + r] = sign[r] * p.R[r * 3 + c];
    m[12 + r] = sign[r] * p.T[r];
  }
  m[15] = 1;
  return m;
}

// scaleIntrinsics is camera.Intrinsics.Scaled: f and c scale with the image.
export function scaleIntrinsics(k: PinholeIntrinsics, width: number, height: number): PinholeIntrinsics {
  const sx = width / k.width, sy = height / k.height;
  return { width, height, fx: k.fx * sx, fy: k.fy * sy, cx: k.cx * sx, cy: k.cy * sy };
}

// intrinsicsProjection is the GL projection that lands world points on the
// same pixels the camera saw: u = fx·x/z + cx with pixel edges at 0 and
// width (openscan's centres-at-+0.5 convention), y flipped for GL.
export function intrinsicsProjection(k: PinholeIntrinsics, near: number, far: number): Mat4 {
  const m = new Float32Array(16);
  m[0] = (2 * k.fx) / k.width;
  m[5] = (2 * k.fy) / k.height;
  m[8] = 1 - (2 * k.cx) / k.width;
  m[9] = (2 * k.cy) / k.height - 1;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

export function cameraCenter(p: CameraPose): Vec3 {
  return poseCameraCenter(p.R, p.T);
}

// frustumCorners returns the world positions of the image corners TL, TR,
// BR, BL pushed out to `depth` metres in front of the camera.
export function frustumCorners(p: CameraPose, k: PinholeIntrinsics, depth: number): Vec3[] {
  const pix: [number, number][] = [[0, 0], [k.width, 0], [k.width, k.height], [0, k.height]];
  return pix.map(([u, v]) => {
    const xc = ((u - k.cx) / k.fx) * depth, yc = ((v - k.cy) / k.fy) * depth, zc = depth;
    const d = [xc - p.T[0], yc - p.T[1], zc - p.T[2]];
    // Xw = Rᵀ·(Xc − T)
    return [
      p.R[0] * d[0] + p.R[3] * d[1] + p.R[6] * d[2],
      p.R[1] * d[0] + p.R[4] * d[1] + p.R[7] * d[2],
      p.R[2] * d[0] + p.R[5] * d[1] + p.R[8] * d[2],
    ] as Vec3;
  });
}
